import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import SectionLabel from "@/components/SectionLabel";
import usePageMeta from "@/hooks/usePageMeta";
import { FAQ } from "@/constants/testIds";

const categories = [
  {
    id: "programma",
    label: "Over het programma",
    items: [
      {
        q: "Wat is LevelLeven precies?",
        a: "LevelLeven START is een intensief groepsprogramma van twaalf weken voor inwoners die langdurig stilstaan en meerdere belemmeringen ervaren. Het doel: bewustwording, eigen regie, vaardigheden, veerkracht en zicht op een passende vervolgstap. Daarna volgt, afhankelijk van de deelnemer en de afspraken met de gemeente, een vervolg richting participatie, scholing, begeleiding of werk. VERDER is een mogelijk vervolg, geen automatische voortzetting.",
      },
      {
        q: "Wat is 'het fundament' en waarom is dat anders?",
        a: "Veel re-integratietrajecten starten bij uitstroom: ze veronderstellen dat iemand al klaar is om stappen te zetten. Maar voor een deel van de doelgroep ontbreekt die basis: geen dagstructuur, geen perspectief, geen gevoel van veiligheid. LevelLeven legt dat fundament. Pas daarna heeft een volgend traject zin.",
      },
      {
        q: "Hoe lang duurt het programma?",
        a: "START duurt twaalf weken. Week 1-3: Startpunt & Rugzak (kennismaken, vertrekpunt vastleggen). Week 4-6: Rem (zelfinzicht en bewustwording). Week 7-9: Vonk & Speelruimte (vaardigheden ervaren en oefenen). Week 10-12: LevelMoment & Volgende beweging (terugblik, eindmeting, passende vervolgstap). De duur van een eventueel vervolg (VERDER) is nog niet vastgesteld.",
      },
      {
        q: "Hoe verschilt LevelLeven van een regulier re-integratietraject?",
        a: "Reguliere re-integratie richt zich op het invullen van vacatures of het aanleren van werkvaardigheden. LevelLeven richt zich op de mens achter de afstand: gedragspatronen, zelfbeeld, dagstructuur en eigenaarschap. LevelLeven is geen vervanging van re-integratie, maar de activerende fase daarvóór.",
      },
      {
        q: "Wat is de methodische basis van het programma?",
        a: "LevelLeven combineert vier bestaande methoden: NLP (gedragsverandering via ervaringsgericht leren), Systemisch werken (patronen zichtbaar maken via opstellingen), Transactionele Analyse (communicatiepatronen herkennen) en de Zelfdeterminatietheorie (motivatie van binnenuit). Het programma is voor 80% gericht op oefening en ervaring, niet op theorie.",
      },
    ],
  },
  {
    id: "doelgroep",
    label: "Over de doelgroep",
    items: [
      {
        q: "Voor wie is LevelLeven bedoeld?",
        a: "Voor inwoners die langdurig aan de zijlijn staan, waarbij meerdere leefgebieden tegelijk onder druk staan: gezondheid, zelfregie, dagstructuur, sociale verbinding. Denk aan mensen die al jaren in de bijstand zitten zonder dat bestaande trajecten beweging hebben gebracht, of mensen die dreigen uit te vallen.",
      },
      {
        q: "Wie kan niet instromen?",
        a: "LevelLeven is niet passend bij acute psychiatrische crises, actieve verslavingsproblematiek zonder behandeling, ernstige agressieproblematiek, acute suïcidaliteit, zware verstandelijke beperkingen of medische situaties die eerst behandeling vereisen. LevelLeven is geen vervanging voor GGZ of crisiszorg en verwijst door waar nodig.",
      },
      {
        q: "Wie bepaalt wie instroomt?",
        a: "Selectie gebeurt door de casemanager of klantmanager van de gemeente, op basis van de in- en uitsluitcriteria die LevelLeven aanlevert. LevelLeven verzorgt een intakegesprek en nulmeting per deelnemer. Deelnemers moeten in groepsverband kunnen functioneren en de Nederlandse taal voldoende beheersen.",
      },
      {
        q: "Wat als een deelnemer tussentijds uitvalt?",
        a: "Uitval wordt zo vroeg mogelijk gesignaleerd via de tweewekelijkse bijeenkomsten en de buddy-structuur. Bij dreigende uitval schakelt de trainer de casemanager in. Als uitval niet te voorkomen is, legt LevelLeven vast wat de reden is en geeft advies over een alternatief vervolgpad. Dit wordt opgenomen in de rapportage aan de gemeente.",
      },
    ],
  },
  {
    id: "financien",
    label: "Financiën en kosten",
    items: [
      {
        q: "Wat kost een traject per deelnemer?",
        a: "Voorlopig uitgangspunt voor de gemeentelijke START-variant: circa €5.000 per deelnemer, afhankelijk van de definitieve uitvoering en afspraken. Rekenvoorbeeld: 20 deelnemers × €5.000 = €100.000 indicatieve groepskosten. Voor VERDER noemen we nog geen prijs. Gebruik gerust de Programmabouwer voor een eigen raming.",
      },
      {
        q: "Hoe verhoudt die prijs zich tot de kosten van een uitkering?",
        a: "Daar doen we bewust geen uitspraak over. Een eerlijke vergelijking vraagt om gemeten resultaten, en die moeten in pilots nog worden verzameld. We beloven geen besparingen of uitstroom.",
      },
      {
        q: "Wanneer is LevelLeven financieel rendabel?",
        a: "Dat weten we pas na onderzoek. Een break-even-punt of terugverdientijd noemen we niet zolang er geen controleerbare onderbouwing is. In een pilot maken we samen afspraken over wat we meten, zodat u daarna zelf kunt beoordelen wat het oplevert.",
      },
      {
        q: "Wat levert het op als deelnemers niet uitstromen naar werk?",
        a: "Werk is niet voor iedereen de eerste stap. Daarom kijken we ook naar eigen regie, dagstructuur, vaardigheden, vertrouwen en een passende vervolgstap. Of dat maatschappelijke kosten verlaagt, is een onderzoeksvraag, geen belofte.",
      },
      {
        q: "Hoe sluit LevelLeven aan op loonkostensubsidie?",
        a: "LevelLeven is zelf geen loonkostensubsidieregeling. De methodiek richt zich op persoonlijke ontwikkeling en mogelijke voorbereiding op participatie of werk. Wanneer een deelnemer later aan de voorwaarden voldoet, kan een werkgever mogelijk gebruikmaken van loonkostensubsidie. LevelLeven wil aansluiten op de bestaande gemeentelijke begeleiding en arbeidsmarktpartners. Loonkostensubsidie financiert het START-programma niet automatisch.",
      },
      {
        q: "Hoe wordt het programma gefinancierd?",
        a: "Meestal vanuit gemeentelijke middelen voor participatie en re-integratie. Hoe dat precies loopt, bespreken we per gemeente. Loonkostensubsidie financiert het START-programma niet automatisch.",
      },
    ],
  },
  {
    id: "uitvoering",
    label: "Uitvoering en samenwerking",
    items: [
      {
        q: "Wie voert het programma uit?",
        a: "Het programma wordt uitgevoerd door gecertificeerde NLP Master Practitioners met jarenlange ervaring in gedragsverandering bij mensen met afstand tot participatie. Per groep zijn altijd twee trainers aanwezig.",
      },
      {
        q: "Hoe werkt de samenwerking met de gemeente?",
        a: "De gemeente fungeert als opdrachtgever en selecteert deelnemers via de casemanager of klantmanager. LevelLeven verzorgt de uitvoering, rapportage en eindverantwoording. Wij werken ook samen met een lokale welzijnspartner die het netwerk in de wijk, buddy-ondersteuning en coachingsbegeleiding levert.",
      },
      {
        q: "Past LevelLeven bij integraal werken?",
        a: "Ja. LevelLeven opereert op het snijvlak van zorg, welzijn, participatie, gezondheid en arbeid. Het programma verbindt deze domeinen in één samenhangende aanpak, zonder dat de gemeente een nieuw systeem hoeft op te zetten.",
      },
      {
        q: "Hoe zit het met rapportage en verantwoording?",
        a: "Een startmeting bij aanvang, een of meer tussenmetingen, een eindmeting na twaalf weken en waar afgesproken een vervolgmeting. Indicatoren stellen we vooraf samen vast. De meetset is nog niet wetenschappelijk gevalideerd.",
      },
      {
        q: "Wanneer kan een pilot starten?",
        a: "Een pilot is haalbaar binnen één begrotingsjaar. Na een intakegesprek en het vaststellen van de deelnemersgroep kan het programma snel van start. De doorlooptijd van eerste contact tot daadwerkelijke start bedraagt gemiddeld 6 tot 10 weken.",
      },
    ],
  },
  {
    id: "beleid",
    label: "Participatiewet & beleid",
    items: [
      {
        q: "Hoe sluit LevelLeven aan op de Participatiewet in Balans?",
        a: "De Participatiewet in Balans legt meer nadruk op maatwerk en vertrouwen. LevelLeven wil daarop aansluiten met een aanvullende ontwikkelfase voor inwoners voor wie werk nog niet de eerste stap is.",
      },
      {
        q: "Hoe is LevelLeven bestuurlijk te verdedigen?",
        a: "Door eerlijk te zijn over wat er wel en niet bekend is. LevelLeven heeft een heldere doelgroep, een uitgewerkte opzet van twaalf weken, een vast meetproces en een indicatieve prijs. Of de aanpak werkt, onderzoeken we samen in een pilot. Zie ook de pagina Onderzoek & onderbouwing.",
      },
      {
        q: "Is LevelLeven iets nieuws of een bestaand product?",
        a: "LevelLeven is een nieuw programma. Het gebruikt inzichten uit bestaande benaderingen. De afzonderlijke onderdelen komen ook elders voor; het onderscheid zoeken we in de samenhang, uitvoering, meetbaarheid en overdracht. De effectiviteit van LevelLeven zelf is nog niet onderzocht.",
      },
    ],
  },
];

export default function Faq() {
  usePageMeta({
    title: "Veelgestelde vragen, LevelLeven",
    description:
      "Antwoorden voor gemeenten, deelnemers en verwijzers, programma, doelgroep, financiën, uitvoering en beleid.",
  });
  return (
    <div>
      <section className="py-16 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl reveal">
            <SectionLabel>Veelgestelde vragen</SectionLabel>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#1D2A22] font-light">
              Eerlijke antwoorden.{" "}
              <span className="italic text-[#E06B3F]">Geen kleine lettertjes.</span>
            </h1>
            <p className="mt-8 text-lg text-[#4A5D4E] font-light leading-relaxed">
              Vijf categorieën met antwoorden op de vragen die wethouders,
              beleidsadviseurs, verwijzers en deelnemers ons stellen.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="space-y-16">
            {categories.map((cat, ci) => (
              <div
                key={cat.id}
                data-testid={`${FAQ.categoryPrefix}-${cat.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-10 reveal"
              >
                <div className="md:col-span-4 lg:sticky lg:top-28 self-start">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-3">
                    {String(ci + 1).padStart(2, "0")}, Categorie
                  </p>
                  <h2 className="font-heading text-3xl sm:text-4xl text-[#1D2A22] leading-tight font-light">
                    {cat.label}
                  </h2>
                </div>
                <div className="md:col-span-7 md:col-start-6">
                  <Accordion type="single" collapsible className="w-full">
                    {cat.items.map((f, i) => (
                      <AccordionItem
                        key={i}
                        value={`${cat.id}-item-${i}`}
                        className="border-b border-[#E5E1D8] border-t-0"
                      >
                        <AccordionTrigger
                          data-testid={`${FAQ.itemPrefix}-${cat.id}-${i}`}
                          className="font-heading text-xl sm:text-2xl text-[#1D2A22] hover:text-[#E06B3F] hover:no-underline py-6 text-left font-light"
                        >
                          {f.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-[#4A5D4E] text-base sm:text-lg leading-relaxed pb-6 font-light">
                          {f.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-24 text-center reveal">
            <p className="text-[#4A5D4E] mb-6 font-light">
              Andere vraag? Wij beantwoorden hem graag persoonlijk.
            </p>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-4 bg-[#1D2A22] hover:bg-[#E06B3F] text-[#FAF7F1] rounded-full pl-8 pr-2 py-2 transition-colors duration-300"
            >
              <span className="text-base font-medium">Stel je vraag</span>
              <span className="bg-[#E06B3F] text-white w-12 h-12 rounded-full flex items-center justify-center">
                <ArrowUpRight size={20} />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
